import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsFetching,
  queryIsLoading,
  useGetFootprintingCropFertilizersMineral,
  useGetFootprintingCropFertilizersOrganic,
  useGetFootprintingCropPrimaryDataById,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useMutateFootprintingCropFertilizersMinerals,
  useMutateFootprintingCropFertilizersOrganics,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_FERTILIZER_UNITS, FootprintingCropFertilizer, PRACTICE_SLUG, ZERO_UUID } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Tooltip,
} from "components";

type CropFertilizersFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

const fertilizerPlaceholder: FootprintingCropFertilizer = {
  id: undefined,
  fertilizerId: "",
  amount: null,
  amountUnit: null,
};

const organicFertilizerPlaceholder: FootprintingCropFertilizer = {
  ...fertilizerPlaceholder,
  nValue: null,
};

export const CropFertilizersForm = ({ farmId, cropId, year, primaryDataId, readOnly }: CropFertilizersFormProps) => {
  const { t } = useTranslation(["footprinting", "practices"]);
  const formRef = useRef<FormInstance>(null);

  const [mineralFertilizerActive, setMineralFertilizerActive] = useState<boolean | null>(null);
  const [organicFertilizerActive, setOrganicFertilizerActive] = useState<boolean | null>(null);
  const [appliedMineralFertilizers, setAppliedMineralFertilizers] = useState<FootprintingCropFertilizer[]>([]);
  const [appliedOrganicFertilizers, setAppliedOrganicFertilizers] = useState<FootprintingCropFertilizer[]>([]);
  const [limeT, setLimeT] = useState<number | null>(null);

  // GET Queries
  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const mineralFertilizerDataQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerDataQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const cropFertilizersMineralQuery = useGetFootprintingCropFertilizersMineral(primaryDataId ?? "");
  const cropFertilizersOrganicQuery = useGetFootprintingCropFertilizersOrganic(primaryDataId ?? "");

  // Mutations
  const { mutate: mutateCropFertilizersMinerals } = useMutateFootprintingCropFertilizersMinerals(
    farmId,
    cropId,
    year,
    appliedMineralFertilizers.filter((f) => !!f.fertilizerId),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropFertilizersOrganics } = useMutateFootprintingCropFertilizersOrganics(
    farmId,
    cropId,
    year,
    appliedOrganicFertilizers.filter((f) => !!f.fertilizerId),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      limeT,
      mineralFertilizerActive,
      organicFertilizerActive,
    },
    primaryDataId
  );

  const isFetching = [cropPrimaryDataQuery, cropFertilizersMineralQuery, cropFertilizersOrganicQuery].some(queryIsFetching);
  const isLoading = [cropPrimaryDataQuery, cropFertilizersMineralQuery, cropFertilizersOrganicQuery].some(queryIsLoading);

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess && !isLoading && !isFetching) {
      setMineralFertilizerActive(cropPrimaryDataQuery.data.mineralFertilizerActive ?? null);
      setOrganicFertilizerActive(cropPrimaryDataQuery.data.organicFertilizerActive ?? null);
      setLimeT(cropPrimaryDataQuery.data.limeT ?? null);

      formRef.current?.setFieldsValue({
        mineralFertilizerActive: cropPrimaryDataQuery.data.mineralFertilizerActive ?? null,
        organicFertilizerActive: cropPrimaryDataQuery.data.organicFertilizerActive ?? null,
        limeT: cropPrimaryDataQuery.data.limeT ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["mineralFertilizerActive", "organicFertilizerActive", "limeT"] as const).some(
        (field) => cropPrimaryDataQuery.data?.[field] !== null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess, isLoading, isFetching]);

  useEffect(() => {
    if (cropFertilizersMineralQuery.data && !isLoading) {
      setAppliedMineralFertilizers(
        cropFertilizersMineralQuery.data.length
          ? cropFertilizersMineralQuery.data
              .filter((application) => application.id !== undefined)
              .map((application) => ({
                id: application.id,
                fertilizerId: application.fertilizerId,
                amount: application.amount,
                amountUnit: application.amountUnit,
              }))
          : [{ ...fertilizerPlaceholder }]
      );
    }
  }, [cropFertilizersMineralQuery.data, isLoading]);

  useEffect(() => {
    if (cropFertilizersOrganicQuery.data && !isLoading) {
      setAppliedOrganicFertilizers(
        cropFertilizersOrganicQuery.data.length
          ? cropFertilizersOrganicQuery.data
              .filter((application) => application.id !== undefined)
              .map((application) => ({
                id: application.id,
                fertilizerId: application.fertilizerId,
                nValue: application.nValue,
                amount: application.amount,
                amountUnit: application.amountUnit,
              }))
          : [{ ...organicFertilizerPlaceholder }]
      );
    }
  }, [cropFertilizersOrganicQuery.data, isLoading]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
        mutateCropFertilizersMinerals();
        mutateCropFertilizersOrganics();
      }
    },
    [isLoading, mutateCropFertilizersMinerals, mutateCropFertilizersOrganics, mutateCropPrimaryData, readOnly]
  );

  // This ensures that all form fields rerender when the non-last mineral
  // fertilizer is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedMineralFertilizers.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedMineralFertilizers.length]);

  // This ensures that all form fields rerender when the non-last organic
  // fertilizer is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedOrganicFertilizers.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedOrganicFertilizers.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropFertilizerForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropFertilizerForm.info")}</p>
        </Flex>
      </Card>

      <Form dataTestId="footprinting-crop-fertilizers-form" ref={formRef}>
        <Card className="padding-100 margin-bottom-100">
          <h3 className="padding-100">{t("cropFertilizerForm.synthetic.subtitle")}</h3>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <span style={{ maxWidth: "60%" }}>
              {t("cropFertilizerForm.synthetic.question")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropFertilizerForm.tooltips.syntheticFertilizerQuestion")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <div className="fertilizer-radio-container">
              <KlimRadioGroup
                required
                name="mineralFertilizerActive"
                dataTestId="footprinting-crop-mineral-fertilizer-radio"
                disabled={readOnly}
                vertical={false}
                type="tertiary"
                size="large"
                onChange={({ target }) => {
                  setMineralFertilizerActive(target.value);
                }}
                value={mineralFertilizerActive}
                defaultValue={mineralFertilizerActive}
                optionType="button"
                options={[true, false].map((opt) => ({
                  label: t(`radioAnswer.${opt}`),
                  value: opt,
                }))}
              />
            </div>
          </Flex>
          {mineralFertilizerActive &&
            appliedMineralFertilizers.map((fertilizer, index) => {
              const selectGroupWithIndex = `footprinting-crop-mineral-fertilizer-select-${index}`;
              const updateItem = (item: FootprintingCropFertilizer) => {
                const next = [...appliedMineralFertilizers];
                next[index] = { ...item };
                setAppliedMineralFertilizers(next);
              };

              return (
                <Form.Item key={index} className="padding-100">
                  <Flex className="margin-bottom-100" justify="space-between" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("cropFertilizerForm.application.title") + (index + 1)}</h5>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedMineralFertilizers([...appliedMineralFertilizers].filter((_, i) => i !== index))}
                    />
                  </Flex>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    className="margin-bottom-100"
                    disabled={readOnly}
                    name={selectGroupWithIndex}
                    dataTestId={selectGroupWithIndex}
                    label={t("cropFertilizerForm.application.productName")}
                    tooltip={t("cropFertilizerForm.tooltips.syntheticProductName")}
                    rules={[{ required: true, message: false }]}
                    initialValue={fertilizer.fertilizerId}
                    value={fertilizer.fertilizerId}
                    onChange={(v) => updateItem({ ...fertilizer, fertilizerId: v })}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    optionFilterProp="children"
                    showSearch
                  >
                    {(mineralFertilizerDataQuery?.data ?? [])
                      .filter(({ id }) => id !== ZERO_UUID)
                      .map((fertilizer) => (
                        <Option
                          disabled={readOnly}
                          key={`${selectGroupWithIndex}-${fertilizer.id}`}
                          data-testid={`${selectGroupWithIndex}-${fertilizer.id}`}
                          value={fertilizer.id}
                        >
                          {t(`${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${fertilizer.translationSlug}`, { ns: "practices" })}
                        </Option>
                      ))}
                  </Select>

                  <Form.Item className="padding-top-50 padding-bottom-50">
                    <InputNumberWithUnitSelector
                      className="floating-label"
                      disabled={readOnly}
                      name={`${selectGroupWithIndex}-amount`}
                      dataTestId={`${selectGroupWithIndex}-amount`}
                      label={t("cropFertilizerForm.application.amount")}
                      unitLabel={t("unit", { ns: "footprinting" })}
                      tooltip={t("cropFertilizerForm.tooltips.syntheticAmountUsed")}
                      units={["tonnes", "kilograms", "cubic_meters", "liters"]}
                      value={fertilizer.amount}
                      initialValue={fertilizer.amount}
                      unitValue={fertilizer.amountUnit}
                      onChange={(v, unit) => updateItem({ ...fertilizer, amount: v, amountUnit: unit as ENUM_FERTILIZER_UNITS })}
                      rules={[
                        { required: true, message: t("cropFertilizerForm.application.amountError") },
                        { type: "number", message: t("cropFertilizerForm.error.totalAmountRequiredError") },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Form.Item>
              );
            })}
          {mineralFertilizerActive && (
            <Button
              type="text"
              disabled={readOnly}
              onClick={() => {
                setAppliedMineralFertilizers([...appliedMineralFertilizers, { ...fertilizerPlaceholder }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropFertilizerForm.synthetic.add")}
            </Button>
          )}
        </Card>
        <Card className="padding-100 margin-bottom-100">
          <h3 className="padding-100">{t("cropFertilizerForm.organic.subtitle")}</h3>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <span style={{ maxWidth: "60%" }}>
              {t("cropFertilizerForm.organic.question")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropFertilizerForm.tooltips.organicFertilizerQuestion")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              required
              name="organicFertilizerActive"
              dataTestId="footprinting-crop-organic-fertilizer-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setOrganicFertilizerActive(target.value);
              }}
              value={organicFertilizerActive}
              defaultValue={organicFertilizerActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {organicFertilizerActive &&
            appliedOrganicFertilizers.map((fertilizer, index) => {
              const selectGroupWithIndex = `footprinting-crop-organic-fertilizer-select-${index}`;
              const updateItem = (item: FootprintingCropFertilizer) => {
                const next = [...appliedOrganicFertilizers];
                next[index] = { ...item };
                setAppliedOrganicFertilizers(next);
              };

              return (
                <Form.Item key={index} className="padding-100">
                  <Flex className="margin-bottom-100" justify="space-between" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("cropFertilizerForm.application.title") + (index + 1)}</h5>
                    <IconButton
                      disabled={readOnly}
                      type="tertiary"
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedOrganicFertilizers([...appliedOrganicFertilizers].filter((_, i) => i !== index))}
                    />
                  </Flex>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    className="margin-bottom-100"
                    disabled={readOnly}
                    name={selectGroupWithIndex}
                    dataTestId={selectGroupWithIndex}
                    label={t("cropFertilizerForm.application.productName")}
                    tooltip={t("cropFertilizerForm.tooltips.organicProductName")}
                    rules={[{ required: false, message: false }]}
                    initialValue={fertilizer.fertilizerId}
                    value={fertilizer.fertilizerId}
                    onChange={(v) => {
                      const nValue = organicFertilizerDataQuery.data?.find((ofd) => ofd.id === v)?.nValue ?? null;
                      updateItem({ ...fertilizer, fertilizerId: v, nValue: nValue });
                      formRef.current?.setFieldValue(`${selectGroupWithIndex}-nitrogen`, nValue);
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                    optionFilterProp="children"
                    showSearch
                  >
                    {(organicFertilizerDataQuery?.data ?? [])
                      .filter(({ id }) => id !== ZERO_UUID)
                      .map((fertilizer) => (
                        <Option
                          disabled={readOnly}
                          key={`${selectGroupWithIndex}-${fertilizer.id}`}
                          data-testid={`${selectGroupWithIndex}-${fertilizer.id}`}
                          value={fertilizer.id}
                        >
                          {t(`${PRACTICE_SLUG.ORGANIC_FERTILIZERS}.${fertilizer.translationSlug}`, { ns: "practices" })}{" "}
                        </Option>
                      ))}
                  </Select>

                  <Form.Item className="padding-bottom-50">
                    <InputNumber
                      name={`${selectGroupWithIndex}-nitrogen`}
                      dataTestId={`${selectGroupWithIndex}-nitrogen`}
                      disabled={readOnly}
                      label={t("cropFertilizerForm.application.nitrogen")}
                      tooltip={t("cropFertilizerForm.tooltips.nitrogenContent")}
                      suffix="%"
                      value={fertilizer.nValue}
                      initialValue={fertilizer.nValue}
                      onChange={(v) => updateItem({ ...fertilizer, nValue: v })}
                      validateFirst={true}
                      precision={2}
                      rules={[
                        { required: true, message: t("cropFertilizerForm.application.amountError") },
                        { type: "number" },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item className="padding-top-50 padding-bottom-50">
                    <InputNumberWithUnitSelector
                      name={`${selectGroupWithIndex}-amount`}
                      dataTestId={`${selectGroupWithIndex}-amount`}
                      disabled={readOnly}
                      label={t("cropFertilizerForm.application.amount")}
                      unitLabel={t("unit", { ns: "footprinting" })}
                      tooltip={t("cropFertilizerForm.tooltips.organicAmountUsed")}
                      units={["tonnes", "kilograms", "cubic_meters", "liters"]}
                      value={fertilizer.amount}
                      initialValue={fertilizer.amount}
                      unitValue={fertilizer.amountUnit}
                      onChange={(v, unit) => updateItem({ ...fertilizer, amount: v, amountUnit: unit as ENUM_FERTILIZER_UNITS })}
                      validateFirst={true}
                      rules={[
                        { required: false, message: t("cropFertilizerForm.application.amountError") },
                        { type: "number", message: t("cropFertilizerForm.application.amountError") },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Form.Item>
              );
            })}
          {organicFertilizerActive && (
            <Button
              type="text"
              disabled={readOnly}
              onClick={() => {
                setAppliedOrganicFertilizers([...appliedOrganicFertilizers, { ...organicFertilizerPlaceholder }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropFertilizerForm.organic.add")}
            </Button>
          )}
        </Card>
        <Card className="padding-100 margin-bottom-100">
          <h3 className="padding-100">{t("cropFertilizerForm.lime.subtitle")}</h3>
          <Form.Item className="padding-100">
            <InputNumber
              disabled={readOnly}
              name="limeT"
              dataTestId="footprinting-crop-fertilizers-form-caco3-input"
              label={t("cropFertilizerForm.application.lime")}
              tooltip={t("cropFertilizerForm.tooltips.lime")}
              suffix="t"
              value={limeT}
              initialValue={limeT}
              precision={2}
              onChange={(v) => setLimeT(v)}
              validateFirst={true}
              rules={[
                { required: true, message: t("cropFertilizerForm.application.amountError") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                  },
                },
              ]}
            />
          </Form.Item>
        </Card>
      </Form>
    </div>
  );
};
